import { parseLevelFromString } from "utils/general";

// The fixed copy for a Collaboration Level, used everywhere a level is shown.
//
// The bolded half of a "Collaboration Level" bullet comes from this mapping,
// driven by the backend's score; the LLM keeps writing only the
// sentence that follows. Before, the whole bullet was the LLM's prose and the
// label was whatever it happened to write.

export const TEAMWORK_LEVEL_NAMES = {
  1: 'Needs Intervention',
  2: 'Needs Guidance',
  3: 'Effective Collaboration',
  4: 'Exceeding Expectations',
};

export const TEAMWORK_LEVEL_NICKNAMES = {
  1: 'Free Agents',
  2: 'Practice Squad',
  3: 'Big Leagues',
  4: 'Dream Team',
};

/**
 * The bolded label for a level, verbatim from the spec:
 * `Collaboration Level 3: Effective Collaboration - "Big Leagues"`.
 *
 * @param {number|string|null|undefined} level A level 1-4.
 * @returns {string|null} The label, or null when the level is not 1-4 — callers
 *   fall back to whatever the LLM wrote rather than showing a malformed label.
 */
export const teamworkLevelLabel = (level) => {
  const key = Number(level);
  const name = TEAMWORK_LEVEL_NAMES[key];
  if (!name) return null;
  return `Collaboration Level ${key}: ${name} - "${TEAMWORK_LEVEL_NICKNAMES[key]}"`;
};

/**
 * The collaboration level a session row shows, from the scores before the prose.
 *
 * `group_collab_level` is the backend's answer: for a session inside the
 * deterministic gate it is the level the live scorers computed during the
 * session, and outside it the level carried on the stored report. Either way it
 * is an integer the row already resolved.
 *
 * `teamwork_level` is the sentence the model wrote. It is read only as a
 * fallback, for a row served before the backend resolved the field — under the
 * current prompt the model no longer writes a level at all, so a surface that
 * reads only this one shows nothing for every gated session.
 *
 * @param {object|null|undefined} value A session row.
 * @returns {number|null} A level 1-4, or null when neither source has one.
 */
export const sessionCollabLevel = (value) => {
  const scored = Number(value?.group_collab_level);
  if (Number.isInteger(scored) && scored >= 1 && scored <= 4) return scored;

  const narrated = parseLevelFromString(value?.teamwork_level);
  return narrated >= 1 && narrated <= 4 ? narrated : null;
};
